import { useSearchParams } from 'react-router-dom';
import { useAsync } from 'react-use';

import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { QueryParam } from '@service-initiation/tech-care-data-access-routing';
import {
  DeliveryMethod,
  companyIdAtom,
  damageTypePageConfirmedAtom,
  damageTypePageValidAsyncAtom,
  deliveryMethodAtom,
  deliverySelectionConfirmedAtom,
  deliverySelectionPageValidAsyncAtom,
  deviceColorOptionIdsAtom,
  deviceTypeAtom,
  fullAddressAtom,
  geolocationAtom,
  postalCodeAtom,
  selectedAppointmentAsyncAtom,
  selectedRepairIdsAtom,
  selectedRepairTypeIdsAtom,
  store,
  storeIdAtom,
  useResetApplicationState,
} from '@service-initiation/tech-care-data-access-store';
import { parseGeolocationUrlParam } from '@service-initiation/tech-care-data-access-tech-care-services';

import { parseIntFromString } from './utils/parseIntFromString';
import { parseNumberArrayFromString } from './utils/parseNumberArrayFromString';

export const Landing = () => {
  const [searchParams] = useSearchParams();
  const [{ loading: loadingNextStep }, goToNextStep] = useGoToNextStep();

  const resetApplicationState = useResetApplicationState();

  const { loading } = useAsync(async () => {
    if (searchParams.toString() === '') {
      resetApplicationState();
      goToNextStep({ replace: true });
    } else {
      resetApplicationState();

      const repairIdParam = searchParams.get(QueryParam.Repair);
      const repairIds = parseNumberArrayFromString(repairIdParam);
      if (repairIds.length > 0) {
        store.set(selectedRepairIdsAtom, repairIds);
      }

      const repairTypeIdParam = searchParams.get(QueryParam.RepairType);
      const repairTypeId = parseIntFromString(repairTypeIdParam);
      if (repairTypeId) {
        store.set(selectedRepairTypeIdsAtom, [repairTypeId]);
      }

      const companyIdParam = searchParams.get(QueryParam.CompanyId);
      const companyId = parseIntFromString(companyIdParam);
      if (companyId) {
        store.set(companyIdAtom, companyId);
      }

      const deviceIdParam = searchParams.get(QueryParam.Device);
      if (deviceIdParam) {
        const deviceId = parseInt(deviceIdParam as string);
        store.set(deviceTypeAtom, { id: deviceId, name: '' });
      }

      const deviceColorOptionIdsParam = searchParams.get(
        QueryParam.DeviceTypeOptionsId
      );
      if (deviceColorOptionIdsParam) {
        const deviceColorOptionIds = deviceColorOptionIdsParam
          ?.split(',')
          .map((id) => parseInt(id));

        store.set(deviceColorOptionIdsAtom, deviceColorOptionIds);
      }

      const fulfillmentParam = searchParams.get(QueryParam.Fulfillment);
      if (
        fulfillmentParam &&
        Object.values(DeliveryMethod).includes(
          fulfillmentParam as DeliveryMethod
        )
      ) {
        store.set(deliveryMethodAtom, fulfillmentParam as DeliveryMethod);
      }

      const postalCodeParam = searchParams.get(QueryParam.PostalCode);
      if (postalCodeParam) {
        store.set(postalCodeAtom, postalCodeParam);
      }

      const fullAddressParam = searchParams.get(QueryParam.FullAddress);
      if (fullAddressParam) {
        store.set(fullAddressAtom, {
          address: fullAddressParam,
          header: fullAddressParam && fullAddressParam.split(',')[0],
        });
      }
      const geolocationParam = searchParams.get(QueryParam.GeoLocation);
      if (geolocationParam) {
        store.set(
          geolocationAtom,
          parseGeolocationUrlParam(JSON.stringify(geolocationParam))
        );
      }
      const storeIdParam = searchParams.get(QueryParam.Store);
      const storeId = parseInt(storeIdParam as string);
      if (storeId) {
        store.set(storeIdAtom, storeId);
      }

      const isDamageTypePageValid = await store.get(
        damageTypePageValidAsyncAtom
      );
      if (isDamageTypePageValid) {
        store.set(damageTypePageConfirmedAtom, true);
      }

      const asapQuickSchedule = searchParams.get(QueryParam.Asap);
      if (asapQuickSchedule === 'true') {
        store.set(selectedAppointmentAsyncAtom, { isAsap: true });
      }
      const isDeliverySelectionValid = await store.get(
        deliverySelectionPageValidAsyncAtom
      );
      if (isDeliverySelectionValid) {
        store.set(deliverySelectionConfirmedAtom, true);
      }
    }

    goToNextStep({ replace: true });
  });

  return loading || loadingNextStep ? (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          <StepContentSkeleton />
        </LeftMainContentSection>
      </MainContentContainer>
    </PageContainer>
  ) : null;
};
